import { Controller } from "@hotwired/stimulus"

const API_KEY_SESSION_STORAGE_TTL_MS = 6 * 60 * 60 * 1000

export default class extends Controller {
  static targets = ["apiKey", "requestForm", "serverUrl", "operationLink"]

  static values = { documentationId: String }

  connect() {
    this.sessionStorageName = `documentation:${this.documentationIdValue}:api-key`
    this.#setDomApiKeyFromSessionStorage()
    this.changeAuthentication()
  }

  changeAuthentication() {
    const authentication = this.element.querySelector("[data-selected-scheme=true]")
    if (authentication === null) {
      return
    }
    const authenticationId = authentication.dataset.schemeId

    this.#setParamInOperationUrl("authentication_id", authenticationId)
    this.#setParamUrl("authentication_id", authenticationId)

    this.#setDomApiKeyFromSessionStorage()

    // Use to update the request
    const apiKey = this.apiKeyTarget.value
    this.dispatch("updatedApiKey", { detail: { apiKey: apiKey } })
    this.dispatch("changedAuthentication")
  }

  changeServer(event) {
    const server = event.currentTarget.parentElement
    const serverId = server.dataset.id

    this.#setParamInOperationUrl("server_id", serverId)
    this.#setParamUrl("server_id", serverId)

    // TODO EXPLORER: Code to move in other file
    const clickedButton = event.currentTarget
    // Firstly, the url is updated first with values in the explorer-server-variables controller,
    // then url will be shared to sendRequestController from this controller.
    this.serverUrlTarget.textContent = server.dataset.rawUrl
    this.serverUrlTarget.dataset.currentServerValues = server.dataset.serverVariables
    this.serverUrlTarget.dataset.currentServerRawUrl = server.dataset.rawUrl
    this.serverUrlTarget.dataset.currentServerId = serverId

    const serverVariablesController = this.application.getControllerForElementAndIdentifier(this.element, "explorer-server-variables")
    this.serverUrlTarget.textContent = server.dataset.rawUrl
    serverVariablesController.connect()

    this.element.querySelectorAll("button").forEach((button) => button.classList.remove("selected"))
    clickedButton.classList.add("selected")
    // until here

    this.#setDomApiKeyFromSessionStorage()

    // Use to update the request, only if operation has some ApiKey.
    if (this.#hasApiKey()) {
      const apiKey = this.apiKeyTarget.value
      this.dispatch("updatedApiKey", { detail: { apiKey: apiKey } })
    }
  }

  resetApiKey() {
    this.apiKeyTarget.value = ""
    const apiKeys = this.#retrieveApiKeysFromSessionStorage()
    const serverId = this.#currentServerSessionStorageKey()
    const serverApiKeys = apiKeys[serverId]
    if (serverApiKeys) {
      delete serverApiKeys[this.#currentAuthenticationSessionStorageKey()]
      if (Object.keys(serverApiKeys).length === 0) {
        delete apiKeys[serverId]
      }
    }
    this.#persistApiKeysInSessionStorage(apiKeys)
    this.dispatch("updatedApiKey", { detail: { apiKey: "" } })
  }

  // let users get entered tokens back when switching servers
  // TODO EXPLORER: add js helper debounce
  updateApiKey(event) {
    const apiKey = event.currentTarget.value
    if (apiKey === "") {
      return this.resetApiKey()
    }
    const apiKeys = this.#retrieveApiKeysFromSessionStorage()
    const serverId = this.#currentServerSessionStorageKey()
    if (!apiKeys[serverId]) {
      apiKeys[serverId] = {}
    }
    apiKeys[serverId][this.#currentAuthenticationSessionStorageKey()] = this.#sessionStorageApiKeyEntry(apiKey)
    this.#persistApiKeysInSessionStorage(apiKeys)
    this.dispatch("updatedApiKey", { detail: { apiKey: apiKey } })
  }

  #setParamUrl(key, value) {
    let params = new URLSearchParams(window.location.search)
    params.set(key, value)
    const newUrl = new URL(window.location)
    newUrl.search = params.toString()
    history.pushState({}, "", newUrl)
  }

  #setDomApiKeyFromSessionStorage() {
    if (!this.#hasApiKey()) {
      return
    }
    const apiKey = this.#getSessionStorageApiKey() || ""
    this.apiKeyTarget.value = apiKey
  }

  #retrieveApiKeysFromSessionStorage() {
    const storedApiKeys = JSON.parse(sessionStorage.getItem(this.sessionStorageName)) || {}
    const apiKeys = this.#unexpiredSessionStorageApiKeys(storedApiKeys)

    if (JSON.stringify(apiKeys) !== JSON.stringify(storedApiKeys)) {
      this.#persistApiKeysInSessionStorage(apiKeys)
    }

    return apiKeys
  }

  #unexpiredSessionStorageApiKeys(storedApiKeys) {
    const now = Date.now()
    const apiKeys = {}

    Object.entries(storedApiKeys).forEach(([serverId, serverApiKeys]) => {
      const entries = Object.entries(serverApiKeys).filter(([, entry]) => entry.expiresAt > now)
      if (entries.length > 0) {
        apiKeys[serverId] = Object.fromEntries(entries)
      }
    })

    return apiKeys
  }

  #persistApiKeysInSessionStorage(apiKeys) {
    sessionStorage.setItem(this.sessionStorageName, JSON.stringify(apiKeys))
  }

  #sessionStorageApiKeyEntry(apiKey) {
    return {
      value: apiKey,
      expiresAt: Date.now() + API_KEY_SESSION_STORAGE_TTL_MS
    }
  }

  #currentServerSessionStorageKey() {
    return this.serverUrlTarget.dataset.currentServerId.trim()
  }

  #currentAuthenticationSessionStorageKey() {
    const authentication = this.element.querySelector("[data-selected-scheme=true]")
    return authentication.dataset.schemeId
  }

  #setParamInOperationUrl(key, value) {
    if (!value) {
      return
    }

    this.operationLinkTargets.forEach((operationLink) => {
      const url = new URL(operationLink.href)
      let operationLinkParams = new URLSearchParams(url.search)

      operationLinkParams.set(key, value)

      url.search = operationLinkParams.toString()
      operationLink.href = url.toString()
    })
  }

  #hasApiKey() {
    return this.apiKeyTargets.length > 0
  }

  #getSessionStorageApiKey() {
    const apiKeys = this.#retrieveApiKeysFromSessionStorage()
    const serverApiKeys = apiKeys[this.#currentServerSessionStorageKey()]
    if (!serverApiKeys) {
      return null
    }
    const entry = serverApiKeys[this.#currentAuthenticationSessionStorageKey()]
    return entry ? entry.value : null
  }
}
